import { Button, Stack } from '@mui/material';
import {
  ExecutionCreationRule,
  ManageServiceKind,
  serviceKindsQuery,
  serviceKindsService,
  ServiceKindTypeEnum,
  UnitOfMeasure
} from '@store/settings/serviceKinds';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';

import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';

import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import MultiSelectComponent from '@components/inputs/multiSelect/MultiSelect.component';
import SelectComponent from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import ModalComponent from '@components/modal/Modal.component';
import { useObservable } from '@ngneat/react-rxjs';
import { ExecutionStatus } from '@store/operations/executions';

interface ManageServiceKindModalProps {
  isCreation?: boolean;
}

const ManageServiceKindModal = (props: ManageServiceKindModalProps) => {
  const {isCreation} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {serviceKindId} = useParams();

  const [loading, setLoading] = useState(false);

  const [{serviceKinds}] = useObservable(serviceKindsQuery.serviceKinds$);

  const [serviceKind, setServiceKind] = useState<ManageServiceKind>({});

  useEffect(() => {
    if (serviceKindId)
      setServiceKind(serviceKindsQuery.serviceKindById(+serviceKindId) || {});
  }, [serviceKindId]);

  if (!isCreation && !serviceKind.id) return null;

  const updateServiceKind = (name: string) => (value: any) => setServiceKind((state) => ({...state, [name]: value}));

  const handleClose = () => navigate('..', {replace: true});

  const handleSave = () => {
    if (isCreation) {
      setLoading(true);
      serviceKindsService.createServiceKind(serviceKind).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.serviceKinds.success.add'), {variant: 'success'});
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!serviceKindId) return;
      setLoading(true);
      serviceKindsService.updateServiceKind(serviceKindId, serviceKind).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.serviceKinds.success.edit'), {variant: 'success'});
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const errors = {
    kind: getFieldError(serviceKind.kind, FieldValidationType.REQUIRED_STRING),
    code: getFieldError(serviceKind.code, FieldValidationType.REQUIRED_STRING),
    execUnitName1: getFieldError(serviceKind.execUnitName1, FieldValidationType.REQUIRED_STRING),
  };

  return (
    <ModalComponent
      title={t(`settings.serviceKinds.manage.title.${isCreation ? 'add' : 'edit'}`)}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <TextFieldComponent
            disabled
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.kind')}
            placeholder={t('global.writeSomething')}
            handleChange={updateServiceKind('kind')}
            value={serviceKind.kind || ''}
            error={errors.kind}
            required/>
          <TextFieldComponent
            disabled
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.code')}
            placeholder={t('global.writeSomething')}
            handleChange={updateServiceKind('code')}
            value={serviceKind.code || ''}
            error={errors.kind}
            required/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.executionUnit1')}
            placeholder={t('global.selectOption')}
            handleChange={updateServiceKind('execUnitName1')}
            value={serviceKind.execUnitName1}
            error={errors.execUnitName1}
            items={UnitOfMeasure.selectItems()}
            required/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.executionUnit2')}
            placeholder={t('global.selectOption')}
            handleChange={updateServiceKind('execUnitName2')}
            value={serviceKind.execUnitName2}
            items={UnitOfMeasure.selectItems()}/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.serviceKindType')}
            placeholder={t('global.selectOption')}
            handleChange={updateServiceKind('type')}
            value={serviceKind.type}
            items={ServiceKindTypeEnum.selectItems()}/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.executionCreationRule')}
            placeholder={t('global.selectOption')}
            handleChange={updateServiceKind('executionCreationRule')}
            value={serviceKind.executionCreationRule}
            items={ExecutionCreationRule.selectItems()}/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.execDefaultStatus')}
            placeholder={t('global.selectOption')}
            handleChange={updateServiceKind('execDefaultStatus')}
            value={serviceKind.execDefaultStatus}
            items={ExecutionStatus.selectItems()}/>
          <MultiSelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.serviceKinds.manage.autoCreateExecutionFromServiceKind')}
            placeholder={t('global.selectOption')}
            handleChange={updateServiceKind('autoCreateExecutionFromServiceKinds')}
            values={serviceKind.autoCreateExecutionFromServiceKinds}
            items={serviceKinds.map((sk) => ({value: sk.id, label: sk.kind}))}/>
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('global.translateKey')}
            placeholder={t('global.writeSomething')}
            handleChange={updateServiceKind('kindTranslateKey')}
            value={serviceKind.kindTranslateKey || ''}/>
          <CheckboxComponent labelWidth={160} label={t('settings.serviceKinds.manage.flatRate')} value={serviceKind.flatRate} handleChange={updateServiceKind('flatRate')} />
          <CheckboxComponent labelWidth={160} label={t('settings.serviceKinds.manage.other')} value={serviceKind.other} handleChange={updateServiceKind('other')} />
        </Stack>
      )}
      actions={(
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={loading || checkIfErrors(errors)}
            onClick={handleSave}
            color="primary">
            {t('global.save')}
          </Button>
        </Stack>
      )}/>
  );
};

export default ManageServiceKindModal;
